import { useEffect, useRef, useState } from "react";
import {
  getAdminNutrition,
  previewCombo,
  saveNutrition,
  searchComboProducts,
} from "../../../network/service/NutritionService";
import { EMPTY, MICRONUTRIENTS, fromProfile, macrosValid, toSaveBody } from "../nutrition/nutrition-model";
import { gradeName } from "../nutrition/grade-names";
import "./combo-builder.css";

const CUSTOM = "grams";
/** "500 g", "1kg", "1 L": the label already says the weight. */
const WEIGHED_LABEL = /\d\s*(k?g|ml|l)\b/i;

let nextKey = 1;
const keyed = (item) => ({ ...item, key: nextKey++ });

/** A saved / searched item as an editable row. */
const toRow = (item) =>
  keyed({
    productId: item.productId ?? item.id,
    name: item.name,
    packs: item.packs ?? [],
    priceId: item.priceId ?? null,
    grams: item.grams ? String(item.grams) : "",
    quantity: String(item.quantity ?? 1),
    hasNutrition: item.hasNutrition,
    verified: item.verified,
  });

/** What the API takes; null while a row is incomplete. */
const toItem = (row) => {
  const quantity = Number(row.quantity);
  if (!(quantity > 0)) return null;
  if (row.priceId) return { productId: row.productId, priceId: row.priceId, quantity };
  const grams = Number(row.grams);
  if (row.grams !== "" && !(grams > 0)) return null;
  return { productId: row.productId, ...(grams > 0 && { grams }), quantity };
};

const kg = (grams) => (grams >= 1000 ? `${Math.round(grams / 10) / 100} kg` : `${Math.round(grams)} g`);
const fmt = (value, unit, dp = 1) =>
  value === null || value === undefined ? "—" : `${Math.round(value * 10 ** dp) / 10 ** dp}${unit ? ` ${unit}` : ""}`;

const FACTS = [
  ["calories", "Energy", "kcal", 0],
  ["protein", "Protein", "g", 1],
  ["carbs", "Carbohydrates", "g", 1],
  ["fat", "Fat", "g", 1],
  ["fiber", "Fibre", "g", 1],
  ["sugar", "Sugars", "g", 1],
  ["saturatedFat", "Saturated fat", "g", 1],
  ["sodium", "Sodium", "mg", 0],
];

/** Values an admin can fill in for one item, right here in the pack. */
const FIX_FIELDS = {
  calories: ["Energy", "kcal"],
  protein: ["Protein", "g"],
  carbs: ["Carbohydrates", "g"],
  fat: ["Fat", "g"],
  sugar: ["Sugars", "g"],
  saturatedFat: ["Saturated fat", "g"],
  sodium: ["Sodium", "mg"],
  addedSugar: ["Added sugars", "g"],
  fiber: ["Fibre", "g"],
  transFat: ["Trans fat", "g"],
  cholesterol: ["Cholesterol", "mg"],
};
/** A product with no nutrition at all: what it needs to be counted and graded. */
const NEW_ITEM_FIELDS = ["calories", "protein", "carbs", "fat", "sugar", "saturatedFat", "sodium", "fiber"];
const FACT_LABEL = (key) => (FIX_FIELDS[key]?.[0] ?? key).toLowerCase();
/** What the grade needs; the flag names these, the form asks for every missing value. */
const KEY_FACTS = ["sugar", "saturatedFat", "sodium", "fiber", "addedSugar"];
const missingLabel = (missing) => missing.filter((f) => KEY_FACTS.includes(f)).map(FACT_LABEL).join(", ");

/** Pack labels in plain words: "12 Nos" -> "12 pieces", "1 No" -> "1 piece". */
const packName = (label) =>
  label.replace(/(\d+(?:\.\d+)?)\s*nos?\b\.?/gi, (_, n) => `${n} ${Number(n) === 1 ? "piece" : "pieces"}`);

/**
 * Combos and weekly / monthly packs: pick the catalog products in one pack
 * and the nutrition, grade and per-day values are summed from them. The
 * page saves the contents with the product (onChange reports them); the
 * server recalculates the pack whenever an item's nutrition changes.
 */
function ComboBuilder({ productId, initial, onChange }) {
  const [rows, setRows] = useState(() => (initial?.items ?? []).map(toRow));
  const [preview, setPreview] = useState(initial?.items?.length ? initial : null);
  const [previewError, setPreviewError] = useState("");
  const [fixing, setFixing] = useState(null);
  // Bumped after an item's nutrition is filled in here: the totals are worked out again.
  const [refresh, setRefresh] = useState(0);
  const changed = useRef(false);

  const edit = (fn) => {
    changed.current = true;
    setRows(fn);
  };
  const update = (key, updates) => edit((list) => list.map((r) => (r.key === key ? { ...r, ...updates } : r)));
  const remove = (key) => edit((list) => list.filter((r) => r.key !== key));
  const move = (key, by) =>
    edit((list) => {
      const i = list.findIndex((r) => r.key === key);
      const j = i + by;
      if (j < 0 || j >= list.length) return list;
      const next = [...list];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  const add = (product, pack) => {
    // The same pack again: one more of it rather than a second row.
    const same = rows.find((r) => r.productId === product.id && pack && r.priceId === pack.priceId);
    if (same) {
      update(same.key, { quantity: String((Number(same.quantity) || 0) + 1) });
      return;
    }
    edit((list) => [...list, toRow({ ...product, productId: product.id, priceId: pack?.priceId ?? null, quantity: 1 })]);
  };

  // ---- live preview ----
  const items = rows.map(toItem);
  const complete = rows.length > 0 && items.every(Boolean);
  const itemsKey = JSON.stringify(items);
  useEffect(() => {
    if (!changed.current && refresh === 0) return undefined;
    if (!complete) {
      setPreview(null);
      return undefined;
    }
    let alive = true;
    const timer = setTimeout(() => {
      previewCombo(items)
        .then((r) => {
          if (!alive) return;
          setPreview(r);
          setPreviewError("");
          // Flags on the rows follow the products' current nutrition.
          setRows((list) =>
            list.map((row, i) =>
              r.items?.[i]?.productId === row.productId
                ? { ...row, hasNutrition: r.items[i].hasNutrition, verified: r.items[i].verified }
                : row,
            ),
          );
        })
        .catch((e) => alive && setPreviewError(e.message));
    }, 400);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
    // items is derived from rows; itemsKey tracks its content.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsKey, complete, refresh]);

  // The page saves the contents together with the product.
  useEffect(() => {
    onChange({
      items: complete ? items : null,
      complete,
      empty: rows.length === 0,
      preview,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsKey, complete, preview]);

  // Rows show the grams the server worked out for each item.
  const rowInfo = (index) => preview?.items?.[index] ?? null;
  const nutrition = preview?.nutrition ?? null;
  // Facts each item lacks that leave the pack's values unknown (only when every row is counted).
  // Values the grade uses; others (trans fat, cholesterol) are optional and not flagged.
  const missingOf = (index) => nutrition?.items?.[index]?.missing?.filter((key) => KEY_FACTS.includes(key)) ?? [];
  const problems = preview?.problems ?? [];
  const savedProblem = !changed.current && problems.length === 0 ? initial?.combo?.problem : null;
  const totalGrams = preview?.items?.reduce((sum, i) => sum + (i.totalGrams ?? 0), 0) ?? 0;

  return (
    <div className="combo">
      <ol className="combo-steps">
        <li>Search and add the products in one pack, picking the pack size of each.</li>
        <li>Set how many of each (e.g. 2 × 500 g).</li>
        <li>Fill in anything flagged as missing. Nutrition and the grade are added up automatically.</li>
      </ol>

      <ProductSearch onAdd={add} exclude={productId} />

      {rows.length === 0 ? (
        <div className="combo-empty mt-3">No products in this pack yet. Search above and pick a pack size to add one.</div>
      ) : (
        <div className="table-responsive mt-3">
          <table className="table table-sm align-middle combo-items">
            <thead>
              <tr>
                <th>Product</th>
                <th>Pack size</th>
                <th style={{ width: 90 }}>How many</th>
                <th className="text-end">Weight</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => {
                const info = rowInfo(index);
                const missing = missingOf(index);
                const needsFix = !row.hasNutrition || missing.length > 0;
                return [
                  <tr key={row.key} className={needsFix ? "combo-row-warn" : undefined}>
                    <td>
                      <strong>{row.name ?? `#${row.productId}`}</strong>
                      <div className="nutri-muted">
                        #{row.productId}
                        {!row.hasNutrition && <span className="combo-flag combo-flag-error">No nutrition</span>}
                        {row.hasNutrition && missing.length > 0 && (
                          <span className="combo-flag">Missing {missingLabel(missing)}</span>
                        )}
                        {row.hasNutrition && !row.verified && <span className="combo-flag combo-flag-muted">Not verified</span>}
                        {row.verified && missing.length === 0 && <span className="combo-flag combo-flag-ok">✓ Verified</span>}
                      </div>
                      {(needsFix || !row.verified) && fixing !== row.key && (
                        <button type="button" className="btn btn-sm btn-link px-0" onClick={() => setFixing(row.key)}>
                          {!row.hasNutrition ? "Add nutrition" : missing.length ? "Fill in missing" : "Check & verify"} →
                        </button>
                      )}
                    </td>
                    <td>
                      <select
                        className="form-select form-select-sm"
                        aria-label={`Pack size of ${row.name}`}
                        value={row.priceId ?? CUSTOM}
                        onChange={(e) => update(row.key, { priceId: e.target.value === CUSTOM ? null : Number(e.target.value) })}
                      >
                        {row.packs.map((pack) => (
                          <option key={pack.priceId} value={pack.priceId}>
                            {packName(pack.label)}
                            {pack.grams ? ` (${pack.estimated ? "≈ " : ""}${kg(pack.grams)})` : " (weight unknown)"}
                          </option>
                        ))}
                        <option value={CUSTOM}>Enter grams…</option>
                      </select>
                      {!row.priceId && (
                        <div className="nutri-unit mt-1">
                          <input
                            type="number"
                            min={1}
                            className="form-control form-control-sm"
                            aria-label={`Grams of ${row.name}`}
                            placeholder="grams of one"
                            value={row.grams}
                            onChange={(e) => update(row.key, { grams: e.target.value })}
                          />
                          <em>g</em>
                        </div>
                      )}
                    </td>
                    <td>
                      <input
                        type="number"
                        min={0.1}
                        step="any"
                        className="form-control form-control-sm"
                        aria-label={`How many of ${row.name}`}
                        value={row.quantity}
                        onChange={(e) => update(row.key, { quantity: e.target.value })}
                      />
                    </td>
                    <td
                      className="text-end text-nowrap"
                      title={info?.estimated ? "Usual weight (e.g. 50 g an egg): set the product's serving size for an exact weight" : undefined}
                    >
                      {info?.totalGrams != null ? `${info.estimated ? "≈ " : ""}${kg(info.totalGrams)}` : "—"}
                    </td>
                    <td className="text-end text-nowrap">
                      <button type="button" className="btn btn-sm btn-link" aria-label="Move up" onClick={() => move(row.key, -1)} disabled={index === 0}>
                        ↑
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-link"
                        aria-label="Move down"
                        onClick={() => move(row.key, 1)}
                        disabled={index === rows.length - 1}
                      >
                        ↓
                      </button>
                      <button type="button" className="btn btn-sm btn-link text-danger" aria-label={`Remove ${row.name}`} onClick={() => remove(row.key)}>
                        ×
                      </button>
                    </td>
                  </tr>,
                  fixing === row.key && (
                    <tr key={`${row.key}-fix`} className="combo-fix-row">
                      <td colSpan={5}>
                        <ItemNutritionFix
                          productId={row.productId}
                          name={row.name}
                          fields={row.hasNutrition && missing.length ? missing : NEW_ITEM_FIELDS}
                          onClose={() => setFixing(null)}
                          onSaved={() => {
                            setFixing(null);
                            setRefresh((n) => n + 1);
                          }}
                        />
                      </td>
                    </tr>
                  ),
                ];
              })}
            </tbody>
            {totalGrams > 0 && (
              <tfoot>
                <tr>
                  <th colSpan={3}>
                    {rows.length} product{rows.length === 1 ? "" : "s"}
                  </th>
                  <th className="text-end text-nowrap">{kg(totalGrams)}</th>
                  <th />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}

      {problems.length > 0 && (
        <div className="nutri-status nutri-status-error mt-3">
          Nutrition can't be added up yet:
          <ul className="mb-0">
            {problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </div>
      )}
      {savedProblem && <div className="nutri-status nutri-status-warn mt-3">{savedProblem}</div>}
      {previewError && <div className="nutri-status nutri-status-error mt-3">{previewError}</div>}

      {nutrition && <ComboTotals nutrition={nutrition} />}
    </div>
  );
}

/**
 * Fill in an item's missing values without leaving the pack: they are saved
 * on that product (so every pack holding it gains them) and the pack's
 * totals are worked out again.
 */
function ItemNutritionFix({ productId, name, fields, onSaved, onClose }) {
  const [form, setForm] = useState(null);
  const [values, setValues] = useState({});
  const [verified, setVerified] = useState(false);
  const [state, setState] = useState({ busy: false, error: "" });

  useEffect(() => {
    let alive = true;
    getAdminNutrition(productId)
      .then((data) => {
        if (!alive) return;
        const loaded = data.profile ? fromProfile(data.profile, data.ingredientFacts) : { ...EMPTY };
        setForm(loaded);
        setVerified(!!loaded.isVerified);
      })
      .catch((e) => alive && setState({ busy: false, error: e.message }));
    return () => {
      alive = false;
    };
  }, [productId]);

  const basis = form?.basisGrams || "100";
  const save = async () => {
    const next = { ...form, ...values, isVerified: verified };
    if (!macrosValid(next)) {
      setState({ busy: false, error: "Energy, protein, carbohydrates and fat are needed." });
      return;
    }
    setState({ busy: true, error: "" });
    try {
      await saveNutrition(productId, toSaveBody(next));
      onSaved();
    } catch (e) {
      setState({ busy: false, error: e.message });
    }
  };

  return (
    <div className="combo-fix">
      <div className="combo-fix-head">
        <strong>{name}</strong>
        <span className="nutri-muted">
          Values per {basis} g, from the product's pack label. Saved on the product, so every pack holding it gets them.
        </span>
      </div>
      {!form && !state.error && <p className="nutri-muted mb-0">Loading…</p>}
      {form && (
        <>
          <div className="combo-fix-grid">
            {fields.map((key) => (
              <label key={key} className="nutri-field">
                <span>{FIX_FIELDS[key][0]}</span>
                <div className="nutri-unit">
                  <input
                    type="number"
                    min={0}
                    step="any"
                    className="form-control form-control-sm"
                    value={values[key] ?? form[key] ?? ""}
                    onChange={(e) => setValues((v) => ({ ...v, [key]: e.target.value }))}
                  />
                  <em>{FIX_FIELDS[key][1]}</em>
                </div>
              </label>
            ))}
          </div>
          <label className="form-check mt-2">
            <input type="checkbox" className="form-check-input" checked={verified} onChange={(e) => setVerified(e.target.checked)} />
            <span className="form-check-label">
              I checked this product's values against its pack label (verified). The pack's grade is shown to customers
              once every product in it is verified.
            </span>
          </label>
        </>
      )}
      {state.error && <div className="text-danger mt-2">{state.error}</div>}
      <div className="d-flex gap-2 mt-2">
        <button type="button" className="btn btn-sm btn-purple-bg" onClick={save} disabled={!form || state.busy}>
          {state.busy ? "Saving…" : `Save on ${name}`}
        </button>
        <button type="button" className="btn btn-sm btn-white-bg" onClick={onClose} disabled={state.busy}>
          Cancel
        </button>
      </div>
    </div>
  );
}

/** Whole pack and per 100 g, vitamins and minerals included; what each item adds; the grade. */
function ComboTotals({ nutrition }) {
  const { total, per100g, health } = nutrition;
  const per100 = (key) => (key in per100g ? per100g[key] : total[key] === null ? null : (total[key] * 100) / total.basisGrams);
  // Summed from the products that list them; "at least" when some product does not.
  const partial = nutrition.partial ?? [];
  const atLeast = new Set(partial.map((p) => p.fact));
  const micros = MICRONUTRIENTS.filter(([code]) => typeof total.micronutrients?.[code] === "number");

  return (
    <div className="combo-totals mt-3">
      <div className="combo-totals-head">
        <div>
          <strong>{kg(total.basisGrams)}</strong> in one pack
        </div>
        <span className="pe-grade-chip" style={{ background: health.color }}>
          {health.eatProteinScore !== null ? `${gradeName(health.grade)} · ${health.eatProteinScore}` : "Not yet rated"}
        </span>
      </div>
      {health.eatProteinScore === null && nutrition.unknown.length > 0 && (
        <p className="nutri-muted mb-2">
          Not rated until the products flagged above have their missing values filled in.
        </p>
      )}
      {!nutrition.verified && (
        <p className="nutri-muted mb-2">
          Customers see the grade once every product in the pack has verified nutrition (or an expert approves the pack).
        </p>
      )}

      <div className="table-responsive">
        <table className="table table-sm combo-facts">
          <thead>
            <tr>
              <th />
              <th className="text-end">Whole pack</th>
              <th className="text-end">Per 100 g</th>
            </tr>
          </thead>
          <tbody>
            {FACTS.map(([key, name, unit, dp]) => (
              <tr key={key}>
                <th scope="row">{name}</th>
                <td className="text-end">{fmt(total[key], unit, dp)}</td>
                <td className="text-end">{fmt(per100(key), unit, dp)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {micros.length > 0 && (
        <div className="table-responsive">
          <table className="table table-sm combo-facts">
            <thead>
              <tr>
                <th>Vitamins &amp; minerals</th>
                <th className="text-end">Whole pack</th>
                <th className="text-end">Per 100 g</th>
              </tr>
            </thead>
            <tbody>
              {micros.map(([code, name, unit]) => (
                <tr key={code}>
                  <th scope="row">{name}</th>
                  <td className="text-end">
                    {atLeast.has(code) ? "at least " : ""}
                    {fmt(total.micronutrients[code], unit, 2)}
                  </td>
                  <td className="text-end">
                    {atLeast.has(code) ? "at least " : ""}
                    {fmt((total.micronutrients[code] * 100) / total.basisGrams, unit, 2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {partial.length > 0 && (
        <p className="nutri-muted mb-2">
          "At least": added up from the products that list them; not listed for{" "}
          {partial.map((p) => `${p.name} (${p.missingIn.join(", ")})`).join("; ")}.
        </p>
      )}

      <details>
        <summary>What each product adds</summary>
        <table className="table table-sm mt-2">
          <thead>
            <tr>
              <th>Product</th>
              <th className="text-end">Weight</th>
              <th className="text-end">Energy</th>
              <th className="text-end">Protein</th>
              <th className="text-end">Carbs</th>
              <th className="text-end">Fat</th>
            </tr>
          </thead>
          <tbody>
            {nutrition.items.map((item, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <tr key={i}>
                <td>{item.name}</td>
                <td className="text-end">{kg(item.grams)}</td>
                <td className="text-end">{fmt(item.calories, "kcal", 0)}</td>
                <td className="text-end">{fmt(item.protein, "g")}</td>
                <td className="text-end">{fmt(item.carbs, "g")}</td>
                <td className="text-end">{fmt(item.fat, "g")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>

      {nutrition.unknown.length > 0 && (
        <p className="nutri-muted mt-2 mb-1">
          Left unknown for the pack (adding up the rest would understate it):{" "}
          {nutrition.unknown.map((u) => `${u.name} (missing for ${u.missingIn.join(", ")})`).join("; ")}.
        </p>
      )}
      {nutrition.assumedZero.length > 0 && (
        <details className="mt-1">
          <summary className="nutri-muted">Counted as 0 (whole foods naturally have none)</summary>
          <p className="nutri-muted mb-0 mt-1">{nutrition.assumedZero.join("; ")}.</p>
        </details>
      )}
    </div>
  );
}

/** Search the catalog; a product is added by picking one of its pack sizes (or grams). */
function ProductSearch({ onAdd, exclude }) {
  const [text, setText] = useState("");
  const [results, setResults] = useState([]);
  const [hidden, setHidden] = useState(0);
  const [state, setState] = useState("idle");
  // Products that add to a pack with nothing to fill in (verified, complete).
  const [readyOnly, setReadyOnly] = useState(true);

  useEffect(() => {
    const q = text.trim();
    if (q.length < 2) {
      setResults([]);
      setState("idle");
      return undefined;
    }
    let alive = true;
    setState("busy");
    const timer = setTimeout(() => {
      searchComboProducts(q, readyOnly)
        .then((page) => {
          if (!alive) return;
          setResults(page.items.filter((p) => p.id !== exclude));
          setHidden(page.hidden ?? 0);
          setState("done");
        })
        .catch(() => alive && setState("error"));
    }, 250);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [text, exclude, readyOnly]);

  const pick = (product, pack) => {
    onAdd(product, pack);
    setText("");
  };

  return (
    <div className="combo-search">
      <input
        className="form-control"
        aria-label="Search products to add"
        placeholder="🔍 Search products to add, e.g. chicken breast, eggs, toor dal (or a product id)"
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      <label className="form-check mt-1 mb-0">
        <input type="checkbox" className="form-check-input" checked={readyOnly} onChange={(e) => setReadyOnly(e.target.checked)} />
        <span className="form-check-label">
          Only verified products with complete nutrition (the pack is then graded without anything to fill in)
        </span>
      </label>
      {state === "busy" && <p className="nutri-muted mt-1 mb-0">Searching…</p>}
      {state === "error" && <p className="text-danger mt-1 mb-0">Search failed. Try again.</p>}
      {state === "done" && results.length === 0 && (
        <p className="nutri-muted mt-1 mb-0">
          No {readyOnly ? "verified, complete " : ""}products match “{text.trim()}”.
          {readyOnly && hidden > 0 && (
            <>
              {" "}
              {hidden} more {hidden === 1 ? "is" : "are"} not verified or missing values:{" "}
              <button type="button" className="btn btn-sm btn-link p-0 align-baseline" onClick={() => setReadyOnly(false)}>
                show them
              </button>{" "}
              (you can fill in and verify them in the pack).
            </>
          )}
        </p>
      )}
      {state === "done" && results.length > 0 && readyOnly && hidden > 0 && (
        <p className="nutri-muted mt-1 mb-0">
          {hidden} more not verified or incomplete.{" "}
          <button type="button" className="btn btn-sm btn-link p-0 align-baseline" onClick={() => setReadyOnly(false)}>
            Show all
          </button>
        </p>
      )}
      {results.length > 0 && (
        <div className="list-group mt-1 combo-results">
          {results.map((product) => (
            <div key={product.id} className="list-group-item">
              <div className="d-flex justify-content-between gap-2">
                <div>
                  <strong>{product.name}</strong> <span className="nutri-muted">#{product.id}</span>
                  {product.isCombo && <span className="combo-flag combo-flag-muted">Combo</span>}
                  {!product.hasNutrition && <span className="combo-flag combo-flag-error">No nutrition</span>}
                  {product.hasNutrition && !product.verified && <span className="combo-flag combo-flag-muted">Not verified</span>}
                  {product.hasNutrition && product.missing?.length > 0 && (
                    <span className="combo-flag">Missing {missingLabel(product.missing)}</span>
                  )}
                  {product.ready && <span className="combo-flag combo-flag-ok">✓ Verified &amp; complete</span>}
                </div>
                {product.per100g && (
                  <span className="nutri-muted text-nowrap">
                    {product.per100g.calories} kcal · {product.per100g.protein} g protein / 100 g
                  </span>
                )}
              </div>
              <div className="d-flex flex-wrap gap-1 mt-1">
                <span className="nutri-muted me-1">Add:</span>
                {product.packs.map((pack) => (
                  <button key={pack.priceId} type="button" className="btn btn-sm btn-white-bg" onClick={() => pick(product, pack)}>
                    + {packName(pack.label)}
                    {pack.grams && !WEIGHED_LABEL.test(pack.label) && (
                      <span className="nutri-muted"> ({pack.estimated ? "≈ " : ""}{kg(pack.grams)})</span>
                    )}
                  </button>
                ))}
                <button type="button" className="btn btn-sm btn-white-bg" onClick={() => pick(product, null)}>
                  + Enter grams…
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default ComboBuilder;
